import { animate, state, style, transition, trigger } from '@angular/animations';
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';

declare var $: any;

export enum FilterParameterType {
  text = 1,
  select = 2,
  dateRange = 3,
};

export interface FilterSelectItem {
  key: string;
  name: string;
}

export class Filter {
  clearSubject: Subject<void>;
  public deactivateSubject: Subject<string>;
  parameters!: FilterParameter[];

  constructor(readonly singleChoiceMode: boolean = false, readonly canClear = true) {
    this.clearSubject = new Subject<void>();
    this.deactivateSubject = new Subject<string>();
  }

  deactivateParameters(exclude: string) {
    if (!this.singleChoiceMode)
      return;
    this.deactivateSubject.next(exclude);
  }

  static clear(filter: Filter): void {
    filter.clearSubject.next();
  }

  static reset(filter: Filter, value: any) {
    if (!this.isCorrect(filter)) {
      return;
    }

    filter.parameters.forEach(parameter => {
      const parameterValues: KeyValuePair[] = [];

      parameter.keys.forEach(key => {
        parameterValues.push({ key, value: value[key] });
      });

      FilterParameter.setValues(parameter, parameterValues);
    });
  }

  static isFilterEmpty(filter: Filter): boolean {
    for (let i = 0; i < filter.parameters.length; i++) {
      if (FilterParameter.hasValue(filter.parameters[i])) {
        return false;
      }
    }
    return true;
  }

  static apply(filter: Filter, value: any) {
    if (!value) {
      return;
    }

    const parameterValues = this.getValues(filter);
    if (!parameterValues) {
      return;
    }

    parameterValues.forEach(x => value[x.key] = x.value);
  }

  private static getValues(filter: Filter): KeyValuePair[] | undefined {
    if (!this.isCorrect(filter)) {
      return;
    }

    let values: KeyValuePair[] = [];
    filter.parameters
      .filter(x => FilterParameter.hasValue(x))
      .forEach(x => {
        const parameterValues = FilterParameter.getValues(x);
        if (parameterValues) {
          parameterValues.forEach(x => {
            if (x.value) {
              values.push(x);
            }
          });
        }
      });

    return values;
  }

  private static isCorrect(filter: Filter): boolean {
    if (!filter || !filter.parameters || !filter.parameters.length) {
      return false;
    }

    return true;
  }
}

export class FilterParameter {
  values?: any[] = [];

  constructor(public filter: Filter,
    private onlyDefaultValue: boolean = false,
    public keys: string[],
    public type: FilterParameterType,
    public name: string,
    public clearSubject: Subject<void>,
    public deactivateSubject: Subject<string>,
    public isActive: boolean = false,
    public availableValues?: FilterSelectItem[]) {
    this.clearSubject.subscribe(_ => {
      FilterParameter.clear(this);
    });
    this.deactivateSubject.subscribe(exlcude => {
      if (exlcude !== this.name) {
        this.isActive = false;
      }
    })
  }

  public get isOnlyDefaultValue(): boolean {
    return this.onlyDefaultValue;
  }

  static clear(parameter: FilterParameter) {
    parameter.isActive = false;
    if (parameter.isOnlyDefaultValue) {
      return;
    }
    parameter.values = [];
  }

  static hasValue(parameter: FilterParameter): boolean {
    if (!parameter.values || !parameter.values.length || !parameter.isActive) {
      return false;
    }

    switch (parameter?.type) {
      case FilterParameterType.text:
      case FilterParameterType.select:
        return parameter.values.length > 0 && parameter.values[0];

      case FilterParameterType.dateRange:
        return (parameter.values.length > 0 && parameter.values[0]) ||
          (parameter.values.length > 1 && parameter.values[1]);

      default:
        return false;
    }
  }

  static getValues(parameter: FilterParameter): KeyValuePair[] {
    switch (parameter?.type) {
      case FilterParameterType.text:
        return [{ key: parameter.keys[0], value: parameter.values?.[0] }];

      case FilterParameterType.select:
        return [{ key: parameter.keys[0], value: parameter.values }];

      case FilterParameterType.dateRange:
        return [
          { key: parameter.keys[0], value: parameter.values?.[0] },
          { key: parameter.keys[1], value: parameter.values?.[1] }
        ];

      default:
        return [];
    }
  }

  static setValues(parameter: FilterParameter, values: KeyValuePair[]) {
    if (!values) {
      return;
    }

    switch (parameter?.type) {
      case FilterParameterType.text:
        parameter.values = [values[0].value];
        break;

      case FilterParameterType.select:
        const selectValues = !values[0].value ? [] : (values[0].value as string).split(',');
        parameter.values = selectValues;
        break;

      case FilterParameterType.dateRange:
        parameter.values = [values[0].value, values[1].value];
        break;
    }
  }
}

export class KeyValuePair {
  key!: string;
  value: any;
}

@Component({
  standalone: false,
  selector: 'app-filter-element',
  templateUrl: './filter.element.component.html',
  styleUrls: ['./filter.element.component.scss'],
  animations: [
    trigger('animation', [
      transition(':enter', [
        style({ height: '0px' }),
        animate('200ms', style({ height: '*', opacity: 1, visibility: 'visible' }))
      ]),
      transition(':leave', [
        animate('100ms ease-out', style({ height: '0px', opacity: 0, visibility: 'hidden' }))
      ])
    ]),
    trigger('datesAnimation', [
      state('open',
        style({ height: '*', opacity: 1, visibility: 'visible' })
      ),
      state('closed',
        style({ height: '0px', opacity: 0, visibility: 'hidden' })
      ),
      transition('void => closed', []),
      transition('closed => open', [
        animate('200ms')
      ]),
      transition('open => closed', [
        animate('200ms')
      ]),
      transition('closed => void', [])
    ])
  ]
})
export class FilterElementComponent implements OnInit, AfterViewInit {
  isActive!: boolean;

  textValue?: string;
  dateFrom?: string;
  dateTo?: string;
  selectedValues: string[] = [];
  FilterParameterType = FilterParameterType;

  @Input()
  filterElementParameter!: FilterParameter;

  ngOnInit(): void {
    this.filterElementParameter.clearSubject.subscribe(_ => {
      this.isActive = false;
      if (this.filterElementParameter.isOnlyDefaultValue) {
        return;
      }
      switch (this.filterElementParameter.type) {
        case FilterParameterType.text:
          this.textValue = '';
          break;
        case FilterParameterType.dateRange:
          this.dateFrom = 'From';
          this.dateTo = 'To';
          break;
        case FilterParameterType.select:
          this.selectedValues = [];
          break;
      }
    });

    this.filterElementParameter.deactivateSubject.subscribe(exclude => {
      if (this.filterElementParameter.name !== exclude) {
        this.isActive = false;
      }
    });

    this.isActive = this.filterElementParameter.isActive;

    switch (this.filterElementParameter.type) {
      case FilterParameterType.text:
        this.textValue = this.filterElementParameter.values?.[0];
        break;
      case FilterParameterType.dateRange:
        this.dateFrom = this.filterElementParameter.values?.[0] ?
          formatDate(this.filterElementParameter.values?.[0], 'MM/dd/YYYY', 'en') : undefined;
        this.dateTo = this.filterElementParameter.values?.[1] ?
          formatDate(this.filterElementParameter.values?.[1], 'MM/dd/YYYY', 'en') : undefined;
        break;
      case FilterParameterType.select:
        break;
    }
  }

  public get dateFromId(): string {
    return this.filterElementParameter.name + 'dateFrom';
  }

  public get dateToId(): string {
    return this.filterElementParameter.name + 'dateTo';
  }

  ngAfterViewInit(): void {
    if (!this.filterElementParameter.isOnlyDefaultValue) {
      $('.datepicker').datetimepicker({
        format: 'MM/DD/YYYY',
        icons: {
          time: 'fa fa-clock-o',
          date: 'fa fa-calendar',
          up: 'fa fa-chevron-up',
          down: 'fa fa-chevron-down',
          previous: 'fa fa-chevron-left',
          next: 'fa fa-chevron-right',
          today: 'fa fa-screenshot',
          clear: 'fa fa-trash',
          close: 'fa fa-remove'
        }
      });
    }

    const dateChangeEventHandler = (event: any) => {

      const todayElement = $('.day.today');
      if (todayElement) {
        todayElement.removeClass('today');
      }

      if (event.currentTarget.id == this.dateFromId) {
        let momentDate = event.date;
        if (momentDate == false) { //empty date input
          this.dateFrom = 'From';
          this.setDateValues();
          return;
        }
        momentDate.toISOString();
        this.dateFrom = momentDate.format('MM/DD/YYYY');
        this.setDateValues();
      } else if (event.currentTarget.id == this.dateToId) {
        let momentDate = event.date;
        if (momentDate == false) { //empty date input
          this.dateTo = 'To';
          this.setDateValues();
          return;
        }
        momentDate.toISOString();
        this.dateTo = momentDate.format('MM/DD/YYYY');
        this.setDateValues();
      }
    };

    if (this.filterElementParameter.type === FilterParameterType.dateRange) {
      $('.datepicker').on('dp.change', dateChangeEventHandler);
      $('.datepicker').on('dp.show', function () {
        const todayElement = $('.day.today');
        if (todayElement) {
          todayElement.removeClass('today');
        }
      });
      $('.datepicker').on('dp.update', function () {
        const todayElement = $('.day.today');
        if (todayElement) {
          todayElement.removeClass('today');
        }
      });
    }
  }

  hideDateControls(): boolean {
    if (this.filterElementParameter.type === 3 && this.isActive)
      return false;
    return true;
  }

  onIsActiveChange($event: any): void {
    this.filterElementParameter.isActive = $event.target.checked;
    if (this.filterElementParameter.isActive) {
      this.filterElementParameter.filter.deactivateParameters(this.filterElementParameter.name);
    }
  }

  onTextValueChange($event: any): void {
    FilterParameter.setValues(this.filterElementParameter,
      [{ key: this.filterElementParameter.keys[0], value: $event.target.value }]);
  }

  onSelectedValueChange($event: any, key: string): void {
    if (this.selectedValues.includes(key)) {
      this.selectedValues = this.selectedValues.filter(x => x != key);
    }

    if ($event.target.checked) {
      this.selectedValues.push(key);
    }

    FilterParameter.setValues(this.filterElementParameter,
      [
        {
          key: this.filterElementParameter.keys[0],
          value: this.selectedValues.join(',')
        }
      ]);
  }

  setChecked(value: FilterSelectItem): boolean {
    if (this.filterElementParameter.values?.includes(value.key)) {
      return true;
    }

    return false;
  }

  private setDateValues(): void {
    if (this.filterElementParameter.type !== FilterParameterType.dateRange) {
      return;
    }

    FilterParameter.setValues(this.filterElementParameter,
      [
        {
          key: this.filterElementParameter.keys[0],
          value: this.dateFrom === 'From' ? undefined : this.dateFrom
        },
        {
          key: this.filterElementParameter.keys[1],
          value: this.dateTo === 'To' ? undefined : this.dateTo
        }
      ]);
  }
}
